import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { Badge, PersonCell, Modal, StatCard, fullName, useDateFilter, DateFilter, useToast, RoleBadge, ROLE_LABELS, EmployeeFilter, uniqueUsers, useSort, SortTh, LEAVE_LABELS, leaveLabel, POOL_LEAVE_TYPES, isDirty, ScopeTabs, overlapText, overlapShort, fmtLeaveDays, leaveLength } from '../components/UI';
import api from '../utils/api';

const LEAVE_SORT = {
  employee: l => l.user ? `${l.user.lastName} ${l.user.firstName}` : '',
  role: l => ROLE_LABELS[l.user?.role] || '',
  leaveType: l => leaveLabel(l.leaveType),
  startDate: l => String(l.startDate),
  endDate: l => String(l.endDate),
  totalDays: l => l.hours ? l.hours / 8 : Number(l.totalDays),
  status: l => l.status,
};

// Typy w formularzu: najpierw z puli, potem osobne uprawnienia
const TYPE_GROUPS = [
  ['Z puli urlopu wypoczynkowego', ['VACATION', 'ON_DEMAND']],
  ['Osobne uprawnienia (nie zmniejszają puli)', ['OCCASIONAL', 'CHILDCARE', 'CARE_ALLOWANCE', 'PATERNITY', 'MATERNITY', 'PARENTAL', 'SICK', 'UNPAID']],
];
// T97: praca zdalna — osobna grupa, tylko dla uprawnionych (Biuro, koordynatorzy)
const REMOTE_GROUP = ['Praca zdalna (nie urlop)', ['REMOTE_WORK']];

const todayKey = () => new Date().toISOString().slice(0, 10);
// Nowy wniosek startuje z dzisiejszą datą w obu polach
// T142: hours = '' → cały dzień (okres), liczba 1–8 → urlop godzinowy w jednym dniu (tylko wypoczynkowy)
const emptyForm = (leaveType = 'VACATION') => ({ leaveType, startDate: todayKey(), endDate: todayKey(), comment: '', hours: '' });
const dateKey = (d) => String(d).slice(0, 10);

export default function LeavesPage() {
  const { user, isAdmin, canSettleLeaves, canApproveLeaves, hasLeave, refreshUser, canRemoteWork, canAccounting } = useAuth();
  // T97: jakie typy pokazać w formularzu — bez prawa do urlopu zostaje sama praca zdalna
  const typeGroups = [...(hasLeave ? TYPE_GROUPS : []), ...(canRemoteWork ? [REMOTE_GROUP] : [])];
  // Kolumny "Pracownik"/"Rola" i filtr — dla każdego, kto widzi cudze wnioski (T28)
  const seesOthers = canApproveLeaves || canSettleLeaves;
  const [leaves, setLeaves] = useState([]);
  const [balance, setBalance] = useState(null);
  const [filter, setFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);     // wniosek w edycji (null = nowy)
  const [cancelling, setCancelling] = useState(null); // wniosek do wycofania
  const [cancelReason, setCancelReason] = useState('');
  const [form, setForm] = useState(emptyForm());
  const [formStart, setFormStart] = useState(emptyForm()); // stan początkowy — do pytania o porzucenie zmian (T62)
  const [workdays, setWorkdays] = useState(null);   // podgląd dni roboczych w formularzu (T19)
  const [loading, setLoading] = useState(true);
  const [employee, setEmployee] = useState('');
  const df = useDateFilter('year'); // domyślnie cały rok — wniosek na przyszły miesiąc nie "znika" (T31)
  const { show, ToastComponent } = useToast();

  const [scope, setScope] = useState('mine'); // T49: „Moje" / „Zespół"
  const people = uniqueUsers(leaves.map(l => l.user));
  const mineCount = leaves.filter(l => l.userId === user?.id).length;
  const teamCount = leaves.length - mineCount;
  const scoped = seesOthers
    ? leaves.filter(l => scope === 'mine' ? l.userId === user?.id : l.userId !== user?.id)
    : leaves;
  const showWho = seesOthers && scope === 'team';
  // T97: zakładka „Do rozliczenia" (APPROVED) — praca zdalna nie idzie do rozliczenia, więc jej tam nie pokazujemy
  const visible = (employee ? scoped.filter(l => l.userId === employee) : scoped)
    .filter(l => !(filter === 'APPROVED' && l.leaveType === 'REMOTE_WORK'));
  const ls = useSort(visible, LEAVE_SORT, 'startDate');

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (df.from) params.set('from', df.from);
      if (df.to) params.set('to', df.to);
      if (filter) params.set('status', filter);
      const data = await api.getLeaves(params.toString());
      setLeaves(data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [df.from, df.to, filter]);

  // Saldo z serwera przy każdym wejściu i po każdej akcji (T20/T22)
  const loadBalance = useCallback(async () => {
    if (!hasLeave && !canRemoteWork) return;
    try { setBalance(await api.getLeaveBalance()); } catch (err) { console.error(err); }
    refreshUser && refreshUser();
  }, [hasLeave, canRemoteWork]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadBalance(); }, [loadBalance]);

  // Podgląd dni roboczych po zmianie dat
  useEffect(() => {
    if (!showForm || !form.startDate || !form.endDate) { setWorkdays(null); return; }
    let cancelled = false;
    api.getWorkdays(form.startDate, form.endDate).then(r => { if (!cancelled) setWorkdays(r.days); }).catch(() => setWorkdays(null));
    return () => { cancelled = true; };
  }, [showForm, form.startDate, form.endDate]);

  const act = async (fn, okMessage) => {
    try {
      const r = await fn();
      show((r && r.message) || okMessage);
      load(); loadBalance();
    } catch (err) { show(err.message, 'error'); }
  };

  const openNew = (type) => { const f = emptyForm(typeof type === 'string' ? type : (hasLeave ? 'VACATION' : 'REMOTE_WORK')); setEditing(null); setForm(f); setFormStart(f); setShowForm(true); };
  const openEdit = (l) => {
    setEditing(l);
    const f = { leaveType: l.leaveType, startDate: dateKey(l.startDate), endDate: dateKey(l.endDate), comment: l.comment || '', hours: l.hours ? String(l.hours) : '' };
    setForm(f); setFormStart(f);
    setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setEditing(null); };

  const handleSubmit = async () => {
    try {
      if (editing) { await api.updateLeave(editing.id, form); show('Wniosek zapisany!'); }
      else { await api.createLeave(form); show(form.leaveType === 'REMOTE_WORK' ? 'Wniosek o pracę zdalną wysłany!' : 'Wniosek urlopowy wysłany!'); }
      closeForm();
      load(); loadBalance();
    } catch (err) { show(err.message, 'error'); }
  };

  // T130: gdy w tym czasie ktoś z zespołu jest już nieobecny — pytamy przed zatwierdzeniem
  const handleApprove = (id) => {
    const l = leaves.find(x => x.id === id);
    if (l?.overlap && !window.confirm(overlapText(l.overlap) + '\n\nZatwierdzić mimo to?')) return;
    act(() => api.approveLeave(id), 'Zatwierdzono');
  };
  const handleReject = (id) => act(() => api.rejectLeave(id), 'Odrzucono');
  const handleProcess = (id) => act(() => api.processLeave(id), 'Wprowadzono do Enovy');
  const confirmCancel = () => {
    const l = cancelling; setCancelling(null); setCancelReason('');
    act(() => api.cancelLeave(l.id, cancelReason), 'Wniosek wycofany');
  };

  // Lustro reguł z backendu: co zalogowany może zrobić z danym wnioskiem (T21)
  const isMine = (l) => l.userId === user?.id;
  const canEdit = (l) => l.status === 'PENDING' && (isMine(l) || canApproveLeaves);
  const canCancel = (l) => {
    if (['REJECTED', 'CANCELLED'].includes(l.status)) return false;
    // T111: własny — tylko oczekujący (zatwierdzony wycofuje przełożony); admin zawsze
    if (isMine(l)) return l.status === 'PENDING' || isAdmin;
    return canApproveLeaves;
  };

  const isPool = POOL_LEAVE_TYPES.includes(form.leaveType);
  // Limit roczny wybranego typu (na żądanie 4, opieka 2, zasiłek 60) — T72/T73.
  // Wniosek na inny rok → pobieramy limity tamtego roku, żeby podpowiedź nie myliła.
  const formYear = form.startDate ? Number(form.startDate.slice(0, 4)) : null;
  const [yearBalance, setYearBalance] = useState(null);
  useEffect(() => {
    if (!showForm || !formYear || !balance || formYear === balance.year) { setYearBalance(null); return; }
    let cancelled = false;
    api.getLeaveBalance(undefined, formYear).then(b => { if (!cancelled) setYearBalance(b); }).catch(() => setYearBalance(null));
    return () => { cancelled = true; };
  }, [showForm, formYear, balance]);
  const limitSource = yearBalance || balance;
  const limit = limitSource?.limits?.[form.leaveType];

  return (
    <div className="flex flex-col gap-14">
      <div className="page-header">
        <h2 className="page-title">Urlopy</h2>
        <div className="flex gap-6">
          {canRemoteWork && <button className="btn btn-outline" onClick={() => openNew('REMOTE_WORK')} title="Wniosek o pracę zdalną — zatwierdza przełożony">💻 Praca zdalna</button>}
          {hasLeave && <button className="btn btn-primary" onClick={() => openNew('VACATION')}>+ Nowy wniosek</button>}
        </div>
      </div>

      {!hasLeave && !canRemoteWork && (
        <div className="card" style={{ background: '#F8FAFC', fontSize: 12, color: '#64748B' }}>
          Twoja forma zatrudnienia nie przewiduje urlopu wypoczynkowego, więc nie składasz tu wniosków.
          W razie pytań skontaktuj się z przełożonym.
        </div>
      )}

      {seesOthers && <ScopeTabs scope={scope} onChange={setScope} mineCount={mineCount} teamCount={teamCount} />}

      <DateFilter from={df.from} to={df.to} onFromChange={df.setFrom} onToChange={df.setTo}
        onFilter={load} quickFilter={df.quick} onQuickFilter={(q) => { df.handleQuick(q); }}>
        {showWho && <EmployeeFilter people={people} value={employee} onChange={setEmployee} />}
      </DateFilter>

      {!hasLeave && canRemoteWork && balance?.limits?.REMOTE_WORK && canAccounting && (!seesOthers || scope === 'mine') && (
        <div className="flex gap-8 flex-wrap">
          <StatCard label="Praca zdalna" value={`${balance.limits.REMOTE_WORK.remaining} / ${balance.limits.REMOTE_WORK.limit}`} accent="#0891B2" sub="dni w roku" />
        </div>
      )}
      {hasLeave && !isAdmin && balance && (!seesOthers || scope === 'mine') && (
        <div className="flex gap-8 flex-wrap">
          <StatCard label="Przysługuje" value={balance.total} sub={`dni w ${new Date().getFullYear()}`} />
          <StatCard label="Wykorzystane" value={fmtLeaveDays(balance.used)} accent="#2563EB" sub="odbyte" />
          <StatCard label="Zaplanowane" value={fmtLeaveDays(balance.planned)} accent="#D97706" sub="zatwierdzone, przed Tobą" />
          <StatCard label="Pozostało" value={fmtLeaveDays(balance.remaining)} accent="#059669"
            sub={balance.pendingDays ? `${fmtLeaveDays(balance.pendingDays)} czeka na zatwierdzenie` : 'do wykorzystania'} />
          {balance.limits?.ON_DEMAND && (
            <StatCard label="Na żądanie" value={`${balance.limits.ON_DEMAND.remaining} / ${balance.limits.ON_DEMAND.limit}`} accent="#7C3AED" sub="limit roczny, z puli powyżej" />
          )}
          {canRemoteWork && balance.limits?.REMOTE_WORK && canAccounting && (
            <StatCard label="Praca zdalna" value={`${balance.limits.REMOTE_WORK.remaining} / ${balance.limits.REMOTE_WORK.limit}`} accent="#0891B2" sub="dni w roku, poza urlopem" />
          )}
        </div>
      )}

      <div className="card">
        <div className="filter-pills" style={{ marginBottom: 12 }}>
          {[['', 'Wszystkie'], ['PENDING', 'Oczekujące'], ['APPROVED', 'Do wprowadzenia (Enova)'], ['PROCESSED', 'W Enovie'], ['REJECTED', 'Odrzucone'], ['CANCELLED', 'Wycofane']].map(([v, l]) => (
            <button key={v} className={`filter-pill ${filter === v ? 'active' : ''}`} onClick={() => { setFilter(v); }}>{l}</button>
          ))}
        </div>
        {loading ? <div style={{ padding: 20, color: '#94A3B8' }}>Ładowanie...</div> : (
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr>
                {showWho && <SortTh label="Pracownik" k="employee" sort={ls.sort} onSort={ls.toggle} />}
                {showWho && <SortTh label="Rola" k="role" sort={ls.sort} onSort={ls.toggle} />}
                <SortTh label="Typ" k="leaveType" sort={ls.sort} onSort={ls.toggle} />
                <SortTh label="Od" k="startDate" sort={ls.sort} onSort={ls.toggle} />
                <SortTh label="Do" k="endDate" sort={ls.sort} onSort={ls.toggle} />
                <SortTh label="Dni rob." k="totalDays" sort={ls.sort} onSort={ls.toggle} />
                <SortTh label="Status" k="status" sort={ls.sort} onSort={ls.toggle} />
                <th>Rozpatrzył</th>
                <th>Akcja</th>
              </tr></thead>
              <tbody>
                {ls.sorted.map(l => (
                  <tr key={l.id}>
                    {showWho && <td><PersonCell name={fullName(l.user)} /></td>}
                    {showWho && <td><RoleBadge role={l.user?.role} /></td>}
                    <td>
                      {leaveLabel(l.leaveType)}
                      {l.leaveType === 'REMOTE_WORK'
                        ? <div style={{ fontSize: 9, color: '#0891B2' }}>💻 nie urlop</div>
                        : !POOL_LEAVE_TYPES.includes(l.leaveType) && <div style={{ fontSize: 9, color: '#94A3B8' }}>poza pulą</div>}
                    </td>
                    <td>{new Date(l.startDate).toLocaleDateString('pl-PL')}
                      {l.overlap && <div title={overlapText(l.overlap)} style={{ fontSize: 10, color: l.overlap.peak >= 2 ? '#DC2626' : '#B45309', fontWeight: 600, whiteSpace: 'nowrap' }}>⚠ {overlapShort(l.overlap)}</div>}
                    </td>
                    <td>{new Date(l.endDate).toLocaleDateString('pl-PL')}</td>
                    <td><b>{l.hours ? `${l.hours} h` : l.totalDays}</b></td>
                    <td><Badge status={l.status} /></td>
                    <td style={{ fontSize: 10, color: '#64748B' }}>
                      {l.status === 'CANCELLED'
                        ? <>wycofany{l.cancelledAt ? ` · ${new Date(l.cancelledAt).toLocaleDateString('pl-PL')}` : ''}{l.cancelReason ? <><br /><i>„{l.cancelReason}"</i></> : null}</>
                        : l.approvedBy
                          ? <>{fullName(l.approvedBy)}<br /><span style={{ color: '#94A3B8' }}>{l.approvedAt ? new Date(l.approvedAt).toLocaleString('pl-PL', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' }) : ''}</span>{l.approverComment ? <><br /><i>„{l.approverComment}"</i></> : null}</>
                          : '—'}
                    </td>
                    <td>
                      <div className="flex gap-4 flex-wrap">
                        {l.status === 'PENDING' && canApproveLeaves && !isMine(l) && (
                          <>
                            <button className="btn btn-success btn-sm" onClick={() => handleApprove(l.id)}>Zatwierdź</button>
                            <button className="btn btn-danger btn-sm" onClick={() => handleReject(l.id)}>Odrzuć</button>
                          </>
                        )}
                        {l.status === 'PENDING' && !canApproveLeaves && !isMine(l) && (
                          <span style={{ color: '#94A3B8', fontSize: 10 }}>czeka na przełożonego</span>
                        )}
                        {l.status === 'APPROVED' && canSettleLeaves && l.leaveType !== 'REMOTE_WORK' && (
                          <button className="btn btn-primary btn-sm" onClick={() => handleProcess(l.id)} title="Kliknij po wprowadzeniu wniosku do Enovy">Zaakceptowany, wprowadź do Enovy</button>
                        )}
                        {l.status === 'APPROVED' && !canSettleLeaves && l.leaveType !== 'REMOTE_WORK' && (
                          <span style={{ color: '#94A3B8', fontSize: 10 }}>→ Biuro</span>
                        )}
                        {l.status === 'PROCESSED' && (
                          <span style={{ color: '#059669', fontSize: 10 }}>
                            ✓ {l.processedBy ? fullName(l.processedBy) : ''}
                            {l.processedAt ? ` · ${new Date(l.processedAt).toLocaleDateString('pl-PL')}` : ''}
                          </span>
                        )}
                        {canEdit(l) && <button className="btn btn-outline btn-sm" title="Edytuj" onClick={() => openEdit(l)}>✎</button>}
                        {canCancel(l) && <button className="btn btn-outline btn-sm" title="Wycofaj wniosek" onClick={() => { setCancelling(l); setCancelReason(''); }}>Wycofaj</button>}
                        {isMine(l) && !canCancel(l) && ['APPROVED', 'PROCESSED'].includes(l.status) && (
                          <span style={{ color: '#94A3B8', fontSize: 10 }} title="Zatwierdzony wniosek wycofuje przełożony — poproś go o to">🔒 zmiany przez przełożonego</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {ls.sorted.length === 0 && <tr><td colSpan={9} style={{ textAlign: 'center', color: '#94A3B8', padding: 20 }}>Brak wniosków w wybranym okresie</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <Modal title={form.leaveType === 'REMOTE_WORK' ? (editing ? 'Edytuj wniosek o pracę zdalną' : 'Wniosek o pracę zdalną') : (editing ? 'Edytuj wniosek urlopowy' : 'Nowy wniosek urlopowy')} onClose={closeForm} dirty={isDirty(form, formStart)}>
          <div className="flex flex-col gap-14" style={{ gap: 12 }}>
            {editing && !isMine(editing) && (
              <div style={{ fontSize: 12, color: '#64748B' }}>Wniosek pracownika: <b>{fullName(editing.user)}</b>. Zmiana zostanie zapisana w dzienniku zdarzeń.</div>
            )}
            <div className="input-group">
              <label>Rodzaj wniosku</label>
              <select value={form.leaveType} onChange={e => setForm({ ...form, leaveType: e.target.value, hours: e.target.value === 'VACATION' ? form.hours : '' })}>
                {/* edytowany wniosek innego typu (np. przełożony poprawia) — jego typ zostaje na liście */}
                {(typeGroups.some(([, t]) => t.includes(form.leaveType)) ? typeGroups : [...typeGroups, ['Obecny typ', [form.leaveType]]]).map(([label, types]) => (
                  <optgroup key={label} label={label}>
                    {types.map(t => <option key={t} value={t}>{LEAVE_LABELS[t]}</option>)}
                  </optgroup>
                ))}
              </select>
              <div style={{ fontSize: 10, color: '#64748B', marginTop: 3 }}>
                {form.leaveType === 'REMOTE_WORK'
                  ? 'Praca zdalna — to nie urlop: nie zmniejsza puli, zatwierdza przełożony. Wybierz dni, w które pracujesz z domu.'
                  : isPool ? 'Zmniejsza pulę urlopu wypoczynkowego.' : 'Osobne uprawnienie — nie zmniejsza puli 26 dni.'}
              </div>
              {limit && (form.leaveType !== 'REMOTE_WORK' || canAccounting) && (
                <div style={{ fontSize: 11, marginTop: 4, padding: '5px 8px', borderRadius: 6, background: limit.remaining > 0 ? '#EFF6FF' : '#FEF2F2', color: limit.remaining > 0 ? '#1E40AF' : '#991B1B' }}>
                  Limit na rok {limitSource.year}: wykorzystano <b>{limit.used}</b> z <b>{limit.limit}</b> dni
                  {limit.remaining > 0 ? <> — pozostało <b>{limit.remaining}</b>.</> : <> — limit wyczerpany na ten rok.</>}
                </div>
              )}
            </div>
            {form.leaveType === 'VACATION' && (
              <div className="input-group">
                <label>Wymiar</label>
                <div className="filter-pills">
                  <button type="button" className={`filter-pill ${!form.hours ? 'active' : ''}`} onClick={() => setForm(f => ({ ...f, hours: '' }))}>Cały dzień</button>
                  <button type="button" className={`filter-pill ${form.hours ? 'active' : ''}`} onClick={() => setForm(f => ({ ...f, hours: f.hours || '4', endDate: f.startDate }))}>Godzinowy</button>
                </div>
                {form.hours && <div style={{ fontSize: 10, color: '#64748B', marginTop: 3 }}>Godziny zbierają się w puli — każde 8 h to jeden dzień urlopu.</div>}
              </div>
            )}
            {form.hours ? (
              <div className="flex gap-8">
                <div className="input-group flex-1"><label>Dzień</label><input type="date" value={form.startDate} onChange={e => setForm(f => ({ ...f, startDate: e.target.value, endDate: e.target.value }))} /></div>
                <div className="input-group flex-1"><label>Ile godzin</label>
                  <select value={form.hours} onChange={e => setForm(f => ({ ...f, hours: e.target.value }))}>
                    {[1, 2, 3, 4, 5, 6, 7, 8].map(h => <option key={h} value={String(h)}>{h} h</option>)}
                  </select>
                </div>
              </div>
            ) : (
            <div className="flex gap-8">
              {/* Zmiana "Od": "Do" podąża, jeśli było puste albo wcześniejsze niż nowe "Od" */}
              <div className="input-group flex-1"><label>Od</label><input type="date" value={form.startDate}
                onChange={e => { const startDate = e.target.value; setForm(f => ({ ...f, startDate, endDate: (!f.endDate || f.endDate < startDate) ? startDate : f.endDate })); }} /></div>
              <div className="input-group flex-1"><label>Do</label><input type="date" value={form.endDate} min={form.startDate || undefined} onChange={e => setForm({ ...form, endDate: e.target.value })} /></div>
            </div>
            )}
            {form.startDate && form.endDate && workdays !== null && (
              <div style={{ fontSize: 12, padding: '6px 10px', borderRadius: 6, background: workdays > 0 ? '#F0FDF4' : '#FEF2F2', color: workdays > 0 ? '#166534' : '#991B1B' }}>
                {workdays > 0
                  ? (form.hours ? <>Urlop godzinowy: <b>{form.hours} h</b> w dniu roboczym</> : <>Dni roboczych: <b>{workdays}</b> (weekendy i święta nie liczą się)</>)
                  : (form.hours ? 'Ten dzień nie jest dniem roboczym — wybierz inny.' : 'W tym okresie nie ma dnia roboczego — wniosek nie zostanie przyjęty.')}
              </div>
            )}
            <div className="input-group"><label>Komentarz</label><textarea value={form.comment} onChange={e => setForm({ ...form, comment: e.target.value })} placeholder="Opcjonalnie..." /></div>
            {balance && isPool && <div style={{ fontSize: 11, color: '#64748B' }}>Pozostało urlopu: <b>{fmtLeaveDays(balance.remaining)}</b></div>}
            <div className="flex gap-6 justify-end">
              <button className="btn btn-outline" onClick={closeForm}>Anuluj</button>
              <button className="btn btn-primary" onClick={handleSubmit} disabled={workdays === 0}>{editing ? 'Zapisz zmiany' : 'Wyślij wniosek'}</button>
            </div>
          </div>
        </Modal>
      )}

      {cancelling && (
        <Modal title="Wycofać wniosek?" onClose={() => setCancelling(null)} dirty={cancelReason.trim().length > 0}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 12 }}>
              {leaveLabel(cancelling.leaveType)}, {new Date(cancelling.startDate).toLocaleDateString('pl-PL')} – {new Date(cancelling.endDate).toLocaleDateString('pl-PL')} ({cancelling.hours ? `${cancelling.hours} h` : `${cancelling.totalDays} dni rob.`})
              {['APPROVED', 'PROCESSED'].includes(cancelling.status) && POOL_LEAVE_TYPES.includes(cancelling.leaveType) && (
                <div style={{ color: '#059669', marginTop: 4 }}>Dni wrócą do puli.</div>
              )}
            </div>
            <div className="input-group"><label>Powód (opcjonalnie)</label><input value={cancelReason} onChange={e => setCancelReason(e.target.value)} placeholder="np. zmiana planów" /></div>
            <div className="flex gap-6 justify-end">
              <button className="btn btn-outline" onClick={() => setCancelling(null)}>Nie</button>
              <button className="btn btn-danger" onClick={confirmCancel}>Wycofaj</button>
            </div>
          </div>
        </Modal>
      )}
      {ToastComponent}
    </div>
  );
}
